import { forwardRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { colors, shadowS } from '@ardoq/design-tokens';
import { WidgetHeaderBar } from './WidgetHeaderBar';
import { DashboardModes } from '../../types';
import { APIChartWidgetAttributes, ArdoqId } from '@ardoq/api-types';
import { Layout } from 'react-grid-layout';
import {
  DASHBOARD_GAP,
  DASHBOARD_MIN_SCREEN_WIDTH_SMALL,
  WIDGET_ASPECT_HEIGHT,
  WIDGET_ASPECT_WIDTH,
} from '../../consts';
import { FlexBox } from '@ardoq/layout';

const editModeHeightAndWidth = css`
  height: 100%;
  width: 100%;
`;

const getWidgetMinHeight = (h: number): number =>
  WIDGET_ASPECT_HEIGHT * h + (h > 1 ? DASHBOARD_GAP : 0);

const getWidgetAspectRatio = (w: number, h: number): string =>
  `${WIDGET_ASPECT_WIDTH * w} / ${getWidgetMinHeight(h)}`;

/* The stored x and w only mean anything on the full column count, so the single
   column auto-places instead. Widgets render in reading order
   (getSortedWidgetPropsArray sorts by y then x), which auto-placement preserves. */
const getViewModeHeightAndWidth = ({ w, x, y, h }: Layout) => css`
  /* aspect-ratio transfers a definite min-height into an implied minimum width,
     which overflows a column narrower than WIDGET_ASPECT_WIDTH and overlaps the
     neighbouring widget. A definite width leaves it nothing to transfer into, so
     the ratio sizes the widget down to the min-height and the floor takes over
     from there. Not redundant with the grid item stretching by default. */
  width: 100%;
  min-width: 0;
  min-height: ${getWidgetMinHeight(h)}px;
  grid-column: auto / span 1;
  grid-row: auto / span ${h};
  aspect-ratio: ${getWidgetAspectRatio(1, h)};

  @media (min-width: ${DASHBOARD_MIN_SCREEN_WIDTH_SMALL}px) {
    grid-column: ${x + 1} / span ${w};
    grid-row: ${y + 1} / span ${h};
    aspect-ratio: ${getWidgetAspectRatio(w, h)};
  }
`;

type CardProps = {
  $dashboardMode: DashboardModes;
  $isSelected?: boolean;
  $hasErrorOutline?: boolean;
  $hasWarningOutline?: boolean;
  $layout: Layout;
};

export const AlertMessagesContainer = styled(FlexBox)``;

const Card = styled.div<CardProps>`
  background-color: ${colors.surfaceDefault};
  box-shadow: ${shadowS};
  border-radius: 8px;
  align-items: center;
  display: flex;
  flex-direction: column;
  ${({ $dashboardMode, $layout }) =>
    $dashboardMode === DashboardModes.EDIT
      ? editModeHeightAndWidth
      : getViewModeHeightAndWidth($layout)}
  &:hover {
    outline: 2px solid ${colors.borderActionHover};
  }

  ${WidgetHeaderBar} .show-only-on-hover {
    display: none;
  }

  &:hover ${WidgetHeaderBar} .show-only-on-hover {
    display: block;
  }

  ${({ $isSelected, $hasErrorOutline, $hasWarningOutline }: CardProps) =>
    $isSelected
      ? css`
          outline: 2px solid ${colors.borderActionActive};
        `
      : $hasErrorOutline
        ? css`
            outline: 1px solid ${colors.borderErrorStrong};
          `
        : $hasWarningOutline &&
          css`
            outline: 1px solid ${colors.borderWarning};
          `}
`;

type WidgetCardProps = {
  children?: ReactNode;
  dashboardMode: DashboardModes;
  hasError?: boolean;
  hasWarning?: boolean;
  isSelected?: boolean;
  widgetId?: ArdoqId;
} & Pick<APIChartWidgetAttributes, 'layout'>;

const WidgetCard = forwardRef<HTMLDivElement, WidgetCardProps>(
  (
    {
      children,
      widgetId,
      layout,
      dashboardMode,
      isSelected = false,
      hasError,
      hasWarning,
    },
    ref
  ) => (
    <Card
      $dashboardMode={dashboardMode}
      id={widgetId}
      $isSelected={isSelected}
      $layout={layout}
      $hasErrorOutline={hasError}
      $hasWarningOutline={hasWarning}
      ref={ref}
      role="gridcell"
    >
      {children}
    </Card>
  )
);

export { WidgetCard };
